import { type ToggleOption } from '../../../../components/GenericToggleButton'
import { type KpiKeyDonut, type KpiKey, type KpiKeyWithDynamicColumn, type KpiKeyBubble } from '../../redux/changeKpiType'
import { type BubbleKpiState, type DonutKpiState } from '../../types'

export function getKpiTitle (kpiName: KpiKey): string {
  if (kpiName === 'Total Shipped') {
    return 'Shipped'
  }
  return kpiName
}

export function getDynamicColumnForDonutTable (kpiName: KpiKeyWithDynamicColumn): string {
  switch (kpiName) {
    case 'Total Reads':
      return 'Interval Length'
    case 'Extended Coverage Level':
      return 'Extended Coverage Level'
    case 'Signal Breakdown':
      return 'Signal Strength'
    case 'Alarm Breakdown':
      return 'Severity'
    case 'Firmware Breakdown':
      return 'Firmware Version'
    case 'Battery Breakdown':
      return 'Battery Percentage'
    case 'Lifecycle Breakdown':
      return 'LC Transition Date'
    case 'Last Read Alarms':
      return 'Alarm Raised At'
    default:
      return kpiName
  }
}

export function getDonutKpiService (kpiName: KpiKeyDonut): string {
  switch (kpiName) {
    case 'Total Reads':
      return 'kpi-service'
    case 'Extended Coverage Level':
      return 'kpi-service'
    case 'Signal Breakdown':
      return 'kpi-service'
    case 'Alarm Breakdown':
      return 'kpi-service'
    case 'Firmware Breakdown':
      return 'kpi-service'
    case 'Battery Breakdown':
      return 'kpi-service'
    case 'Lifecycle Breakdown':
      return 'kpi-service'
    default:
      return 'kpi-service'
  }
}

export function getKpiService (kpiName: KpiKey): string {
  switch (kpiName) {
    case 'Total Registered':
      return 'kpi-service'
    case 'Total Connected':
      return 'kpi-service'
    case 'Total Shipped':
      return 'kpi-service'
    case 'Total Stale':
      return 'kpi-service'
    case 'Total Reads':
      return 'kpi-service'
    case 'Extended Coverage Level':
      return 'kpi-service'
    case 'Signal Breakdown':
      return 'kpi-service'
    case 'Alarm Breakdown':
      return 'kpi-service'
    case 'Firmware Breakdown':
      return 'kpi-service'
    case 'Last Read Alarms':
      return 'kpi-service'
    case 'Battery Breakdown':
      return 'kpi-service'
    case 'Lifecycle Breakdown':
      return 'kpi-service'
    case 'Spread Factor':
      return 'kpi-service'
    default:
      return 'kpi-service'
  }
}

export function getDetailKeyword (kpiName: KpiKey, barItem: string): string[] {
  if (kpiName === 'Total Registered') {
    if (barItem === 'Shipped') {
      return ['total-registered-shipped-details', 'asset-service']
    }
    return ['total-registered-communicated-details', 'asset-service']
  } else if (kpiName === 'Total Connected') {
    if (barItem === 'Shipped') {
      return ['total-registered-communicated-details', 'asset-service']
    }
    return ['total-connected-details', 'kpi-service']
  } else if (kpiName === 'Total Shipped') {
    return ['total-shipped-details', 'asset-service']
  } else if (kpiName === 'Total Stale') {
    return ['total-stale-details', 'asset-service']
  } else if (kpiName === 'Total Reads') {
    return ['total-reads-details-in-range', 'kpi-service']
  } else if (kpiName === 'Extended Coverage Level') {
    return ['ec-mode-details', 'kpi-service']
  } else if (kpiName === 'Signal Breakdown') {
    return ['signal-strength-details', 'kpi-service']
  } else if (kpiName === 'Alarm Breakdown') {
    return ['alarms-breakdown-details', 'kpi-service']
  } else if (kpiName === 'Firmware Breakdown') {
    return ['firmware-details', 'asset-service']
  } else if (kpiName === 'Last Read Alarms') {
    return ['last-reads-alarms-details', 'kpi-service']
  } else if (kpiName === 'Battery Breakdown') {
    return ['battery-percent-details', 'kpi-service']
  } else if (kpiName === 'Lifecycle Breakdown') {
    return ['asset-details-with-state', 'asset-service']
  } else if (kpiName === 'Spread Factor') {
    return ['spread-factor-details', 'kpi-service']
  } // TODO
  return ['', '']
}

export function getToggleOptions (kpiName: KpiKey): ToggleOption[] {
  if (kpiName === 'Total Registered') {
    return [
      { label: 'Total Shipped', value: 'Shipped' },
      { label: 'Total Communicated Once', value: 'Active' }
    ]
  } else if (kpiName === 'Total Connected') {
    return [
      { label: 'Total Communicated Once', value: 'Shipped' },
      { label: 'Total Communicated in last 7 days', value: 'Active' }
    ]
  } else if (kpiName === 'Total Shipped') {
    return [
      { label: 'Total Shipped', value: 'Shipped' }
    ]
  } else if (kpiName === 'Total Stale') {
    return [
      { label: 'Total Stale', value: 'Shipped' }
    ]
  } else if (kpiName === 'Last Read Alarms') {
    return [
      { label: 'Last Read Alarms', value: 'Last Read Alarms' }
    ]
  } else if (kpiName === 'Spread Factor') {
    return [
      { label: 'Spread Factor', value: 'Spread Factor' }
    ]
  }
  return [{ label: 'Total Shipped', value: 'Shipped' }]
}

export const KPI_NAME_ID_MAPPING: Record<string, KpiKey> = {
  '54c0f6dd-3460-433c-b808-cad310fce27c': 'Data Completeness',
  'd80ed664-1b24-4d58-a445-ebc2b1361847': '30 Days Trend',
  'b5820516-7388-4511-950f-10a9271aa44e': 'Total Shipped',
  '8dfc4b26-43e3-4660-bd5e-bc8b13d8c82b': 'Total Registered',
  'f7b2fe73-83ad-4710-9232-fcad8e644746': 'Total Connected',
  '250d8072-0aff-4f62-93c0-d4696e14285b': 'Total Stale',
  '1b40a027-676e-47fe-ab41-2fd31910ab0b': 'Total Reads',
  '7f354fc3-c943-47a5-831f-d26f55517bd0': 'Extended Coverage Level',
  'f08b9d34-afa3-428f-aeac-c2ff563c8cfa': 'Signal Breakdown',
  '8be54d76-2128-4257-a54f-1fb5ff1473a8': 'Alarm Breakdown',
  '5049cdfb-7943-44ba-a600-34cdcde4b6ef': 'Firmware Breakdown',
  '9327b025-d6da-42a0-83da-af2856b48ab1': 'Last Read Alarms',
  'b9a0aa20-0d73-4319-acb5-2245a7ae76f2': 'Battery Breakdown',
  '013dc232-f475-4869-bf30-eeba83b40bd8': 'Lifecycle Breakdown',
  'ce30b6cd-0ed4-41ec-bcaa-a64c35c3a0e5': 'Spread Factor'
}

export const KPI_NAME_ID_MAPPING_REVERSE: Record<KpiKey, string> = {
  'Data Completeness': '54c0f6dd-3460-433c-b808-cad310fce27c',
  '30 Days Trend': 'd80ed664-1b24-4d58-a445-ebc2b1361847',
  'Total Shipped': 'b5820516-7388-4511-950f-10a9271aa44e',
  'Total Registered': '8dfc4b26-43e3-4660-bd5e-bc8b13d8c82b',
  'Total Connected': 'f7b2fe73-83ad-4710-9232-fcad8e644746',
  'Total Stale': '250d8072-0aff-4f62-93c0-d4696e14285b',
  'Total Reads': '1b40a027-676e-47fe-ab41-2fd31910ab0b',
  'Extended Coverage Level': '7f354fc3-c943-47a5-831f-d26f55517bd0',
  'Signal Breakdown': 'f08b9d34-afa3-428f-aeac-c2ff563c8cfa',
  'Alarm Breakdown': '8be54d76-2128-4257-a54f-1fb5ff1473a8',
  'Firmware Breakdown': '5049cdfb-7943-44ba-a600-34cdcde4b6ef',
  'Last Read Alarms': '9327b025-d6da-42a0-83da-af2856b48ab1',
  'Battery Breakdown': 'b9a0aa20-0d73-4319-acb5-2245a7ae76f2',
  'Lifecycle Breakdown': '013dc232-f475-4869-bf30-eeba83b40bd8',
  'Spread Factor': 'ce30b6cd-0ed4-41ec-bcaa-a64c35c3a0e5'
}

export const getKpiNameFromId = (id: string): KpiKey | undefined => {
  // check if given id is a valid key in KPI_NAME_ID_MAPPING then return the value
  if (id in KPI_NAME_ID_MAPPING) {
    return KPI_NAME_ID_MAPPING[id]
  }
}

export const getKpiIdFromName = (name: KpiKey): string | undefined => {
  // check if given name is a valid key in KPI_NAME_ID_MAPPING_REVERSE then return the value
  if (name in KPI_NAME_ID_MAPPING_REVERSE) {
    return KPI_NAME_ID_MAPPING_REVERSE[name]
  }
}

const donutTypeMap = ['Strong', 'Medium', 'Poor']
const lifecycleDonutTypeMap = ['Active', 'Inactive', 'Shipped', 'Removed']
const totalReadsDonutTypeMap = [15, 30, 60]
const alarmDonutTypeMap = ['Critical', 'High', 'Medium', 'Low']
const kpiBarType = ['ECL MODE 0', 'ECL MODE 1', 'ECL MODE 2']
const kpiBarDetailsType = ['0', '1', '2']

export const getDonutType = (kpiType: KpiKeyDonut, index: number, state: DonutKpiState): string => {
  if (kpiType === 'Extended Coverage Level') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : kpiBarType[index].toString()
  } else if (kpiType === 'Signal Breakdown') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : donutTypeMap[index]
  } else if (kpiType === 'Total Reads') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : totalReadsDonutTypeMap[index].toString()
  } else if (kpiType === 'Alarm Breakdown') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : alarmDonutTypeMap[index]
  } else if (kpiType === 'Firmware Breakdown') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : (index + 1).toString()
  } else if (kpiType === 'Battery Breakdown') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : donutTypeMap[index]
  } else if (kpiType === 'Lifecycle Breakdown') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : lifecycleDonutTypeMap[index]
  } else {
    return 'key-not-present'
  }
}

export const getBubbleType = (kpiType: KpiKeyBubble, index: number, state: BubbleKpiState): string => {
  if (kpiType === 'Spread Factor') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : 'SPREAD FACTOR 7'
  } else {
    return 'key-not-present'
  }
}
const nameToCode: Record<string, string> = {
  'SPREAD FACTOR 7': '0',
  'SPREAD FACTOR 8': '1',
  'SPREAD FACTOR 9': '2',
  'SPREAD FACTOR 10': '3',
  'SPREAD FACTOR 11': '4',
  'SPREAD FACTOR 12': '5'
}
export const getBubbleTypeCode = (kpiType: KpiKeyBubble, index: number, state: BubbleKpiState): string => {
  if (kpiType === 'Spread Factor') {
    return state.data !== undefined ? nameToCode[state.data.liveData[0].dataPoint[index].name] : nameToCode['SPREAD FACTOR 7']
  } else {
    return 'key-not-present'
  }
}

export const getDonutDetails = (kpiType: KpiKeyDonut, index: number, state: DonutKpiState): string => {
  if (kpiType === 'Extended Coverage Level') {
    return kpiBarDetailsType[index].toString()
  } else if (kpiType === 'Signal Breakdown') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : donutTypeMap[index]
  } else if (kpiType === 'Total Reads') {
    return totalReadsDonutTypeMap[index].toString()
  } else if (kpiType === 'Alarm Breakdown') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : alarmDonutTypeMap[index]
  } else if (kpiType === 'Firmware Breakdown') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : (index + 1).toString()
  } else if (kpiType === 'Battery Breakdown') {
    return donutTypeMap[index]
  } else if (kpiType === 'Lifecycle Breakdown') {
    return state.data !== undefined ? state.data.liveData[0].dataPoint[index].name : lifecycleDonutTypeMap[index]
  } else {
    return 'key-not-present'
  }
}
